import { useState } from "react";
import { Shield, ChevronRight, Zap, FlaskConical, Lock } from "lucide-react";

const BETA_STORAGE_KEY = "onpoint_beta_access";

export function hasBetaAccess() {
  return localStorage.getItem(BETA_STORAGE_KEY) === "granted";
}

export function clearBetaAccess() {
  localStorage.removeItem(BETA_STORAGE_KEY);
}

export default function BetaGatePage({ onAccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        localStorage.setItem(BETA_STORAGE_KEY, "granted");
        onAccess();
      } else {
        setError("Invalid beta credentials. Please try again.");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12 overflow-hidden" style={{ backgroundColor: "#282560" }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 30% 50%, #1B75BA 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 w-64 h-64 opacity-5" style={{ background: "radial-gradient(circle, #B8D8F2 0%, transparent 70%)" }} />

        <div className="relative z-10" />

        <div className="relative z-10 flex flex-col items-center text-center">
          <img src="/images/logo-white.png" alt="OnPoint BI" className="w-72 h-auto mb-10" />

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6" style={{ backgroundColor: "rgba(245,158,11,0.2)", border: "1px solid rgba(245,158,11,0.4)" }}>
            <FlaskConical className="w-3.5 h-3.5" style={{ color: "#FCD34D" }} />
            <span className="text-xs font-semibold tracking-wide" style={{ color: "#FCD34D" }}>BETA PROGRAM</span>
          </div>

          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            Early Access Preview
          </h1>
          <p className="text-white/60 text-sm mb-10 leading-relaxed max-w-xs">
            You're invited to experience OnPoint BI before the public launch. Enter your beta credentials to explore the platform.
          </p>

          <div className="space-y-4 text-left w-full max-w-xs">
            {[
              { icon: Zap, text: "AI-powered business intelligence" },
              { icon: Shield, text: "Live CRM & financial analytics" },
              { icon: Lock, text: "Secure, invite-only beta access" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(188,218,241,0.15)", border: "1px solid rgba(188,218,241,0.2)" }}>
                  <Icon className="w-4 h-4" style={{ color: "#B8D8F2" }} />
                </div>
                <span className="text-white/70 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/25 text-xs">&copy; 2026 OnPointBI. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-6 lg:p-14 bg-white relative">
        <div className="w-full max-w-md" style={{ animation: "fadeIn 0.5s ease-out" }}>
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/images/logo-colored.png" alt="OnPoint BI" className="w-56 h-auto" />
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#FEF3C7", border: "1.5px solid #FDE68A" }}>
              <FlaskConical className="w-5 h-5" style={{ color: "#D97706" }} />
            </div>
            <div className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>
              Beta v1.0
            </div>
          </div>

          <div className="mb-8 mt-4">
            <h2 className="text-2xl font-bold mb-1.5" style={{ color: "#1C2040" }}>
              Login to Experience OnPointBI
            </h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>
              Enter your beta credentials to access the preview
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="beta-gate-form">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#3D4363" }}>Username</label>
              <input
                data-testid="beta-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter beta username"
                required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #D9D9D9", color: "#1C2040" }}
                onFocus={(e) => e.target.style.borderColor = "#D97706"}
                onBlur={(e) => e.target.style.borderColor = "#D9D9D9"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#3D4363" }}>Password</label>
              <input
                data-testid="beta-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter beta password"
                required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #D9D9D9", color: "#1C2040" }}
                onFocus={(e) => e.target.style.borderColor = "#D97706"}
                onBlur={(e) => e.target.style.borderColor = "#D9D9D9"}
              />
            </div>

            {error && (
              <div data-testid="beta-error-message" className="text-sm px-3 py-2 rounded-lg" style={{ backgroundColor: "#FEF2F2", color: "#EA580C", border: "1px solid #FED7AA" }}>
                {error}
              </div>
            )}

            <button
              data-testid="beta-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg font-semibold text-sm text-white transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#D97706" }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "#B45309")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#D97706")}
            >
              {loading ? "Verifying..." : (<>Access Beta <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <p className="text-xs leading-relaxed" style={{ color: "#92400E" }}>
              <strong>Beta Notice:</strong> This is an early-access version of OnPoint BI. Features may change, and some functionality is still under active development. Your feedback helps us build a better product.
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <footer className="w-full py-3 px-6 flex items-center justify-between text-xs" style={{ color: "#8A90A7", borderTop: "1px solid #E8EAF0" }}>
            <span>&copy; 2026 OnPointBI. All rights reserved.</span>
            <span style={{ color: "#6B7491" }}>Invite-only Beta</span>
          </footer>
        </div>
      </div>
    </div>
  );
}
